import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import {
  atualizarUsuario,
  criarUsuario,
  gerarSenhaProvisoria,
  liberarMarca,
  listarPapeis,
  listarUsuarios,
  redefinirSenha,
  retirarMarca,
  type PapelAdmin,
  type UsuarioAdmin,
} from '../api/admin'
import { useAuth } from '../state/AuthContext'
import { useFiltros } from '../state/FiltrosContext'

type Novo = { email: string; nome: string; senha: string }
type Criado = { email: string; senha: string; id: string }

/** Quem acessa o sistema, com que papel e quais marcas vê. Remover é desativar. */
export function UsuariosPage() {
  const { user, pode } = useAuth()
  // As marcas que EU vejo: só dá para liberar essas (o banco barra as outras).
  const { marcas } = useFiltros()
  const [usuarios, setUsuarios] = useState<UsuarioAdmin[] | null>(null)
  const [papeis, setPapeis] = useState<PapelAdmin[]>([])
  const [editando, setEditando] = useState<string | null>(null)
  const [novo, setNovo] = useState<Novo | null>(null)
  const [criado, setCriado] = useState<Criado | null>(null)
  const [salvando, setSalvando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)

  const carregar = useCallback(async () => {
    try {
      const [u, p] = await Promise.all([listarUsuarios(), listarPapeis()])
      setUsuarios(u)
      setPapeis(p.papeis)
    } catch (e) {
      setErro((e as Error).message)
    }
  }, [])

  useEffect(() => {
    carregar()
  }, [carregar])

  const alterar = async (acao: () => Promise<unknown>) => {
    setSalvando(true)
    try {
      await acao()
      setErro(null)
    } catch (e) {
      setErro((e as Error).message)
    } finally {
      await carregar()
      setSalvando(false)
    }
  }

  const papelDe = (id: string | null) => papeis.find((p) => p.id === id)
  const veTodas = (u: UsuarioAdmin) => Boolean(papelDe(u.papelId)?.permissoes.has('marcas.ver_todas'))
  // Papel com permissão que eu não tenho: nem atribuo, nem mexo em quem já o tem (o banco barra).
  const alemDoMeu = useCallback((p: PapelAdmin | undefined) => Boolean(p && [...p.permissoes].some((x) => !pode(x))), [pode])
  const usuario = usuarios?.find((u) => u.id === editando)
  const nomesMarcas = useMemo(() => new Map(marcas.map((m) => [m.id, m.nome])), [marcas])

  const criar = async () => {
    if (!novo) return
    const dados = novo
    setSalvando(true)
    try {
      const id = await criarUsuario(dados.email, dados.senha, dados.nome)
      setNovo(null)
      setCriado({ email: dados.email.trim().toLowerCase(), senha: dados.senha, id })
      setErro(null)
      await carregar()
    } catch (e) {
      setErro((e as Error).message)
    } finally {
      setSalvando(false)
    }
  }

  return (
    <Stack spacing={2}>
      <Stack direction="row" alignItems="flex-start" spacing={2} useFlexGap flexWrap="wrap">
        <Box sx={{ mr: 'auto' }}>
          <Typography variant="h5" sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 700, lineHeight: 1.2 }}>
            Usuários
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 720 }}>
            Todo usuário novo entra sem papel e sem marca — não vê nada até receber os dois. Remover é desativar: a
            pessoa perde o acesso na hora.
          </Typography>
        </Box>
        <Button variant="contained" onClick={() => setNovo({ email: '', nome: '', senha: gerarSenhaProvisoria() })} sx={{ textTransform: 'none' }}>
          Novo usuário
        </Button>
      </Stack>

      {erro ? (
        <Alert severity="error" onClose={() => setErro(null)}>
          {erro}
        </Alert>
      ) : null}

      {criado ? (
        <Alert severity="success" onClose={() => setCriado(null)}>
          Usuário <strong>{criado.email}</strong> criado. Passe a senha provisória{' '}
          <Box component="code" sx={{ px: 0.5, fontWeight: 700 }}>
            {criado.senha}
          </Box>{' '}
          para a pessoa (no primeiro acesso ela cria a própria senha) e defina o papel e as marcas dela abaixo.
        </Alert>
      ) : null}

      {!usuarios ? (
        <Box sx={{ display: 'grid', placeItems: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ opacity: salvando ? 0.6 : 1, transition: 'opacity 160ms ease' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Usuário</TableCell>
                <TableCell>Papel</TableCell>
                <TableCell>Marcas</TableCell>
                <TableCell>Situação</TableCell>
                <TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {usuarios.map((u) => (
                <TableRow key={u.id} hover sx={{ opacity: u.ativo ? 1 : 0.6 }}>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {u.nome || u.email}
                      {u.id === user?.id ? ' (você)' : ''}
                    </Typography>
                    {u.nome ? (
                      <Typography variant="caption" color="text.secondary">
                        {u.email}
                      </Typography>
                    ) : null}
                  </TableCell>
                  <TableCell>{papelDe(u.papelId)?.nome ?? <Chip size="small" label="Sem papel" color="warning" variant="outlined" />}</TableCell>
                  <TableCell>
                    {veTodas(u) ? (
                      'Todas'
                    ) : u.marcas.size ? (
                      [...u.marcas].map((m) => nomesMarcas.get(m) ?? 'marca desativada').join(', ')
                    ) : (
                      <Chip size="small" label="Nenhuma" color="warning" variant="outlined" />
                    )}
                  </TableCell>
                  <TableCell>{u.ativo ? 'Ativo' : 'Desativado'}</TableCell>
                  <TableCell align="right">
                    <Button size="small" onClick={() => setEditando(u.id)} sx={{ textTransform: 'none' }}>
                      Editar
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Edição: cada mudança salva na hora e as travas do banco respondem na própria tela. */}
      <Dialog open={Boolean(usuario)} onClose={() => setEditando(null)} fullWidth maxWidth="sm">
        {usuario ? (
          <>
            <DialogTitle>{usuario.nome || usuario.email}</DialogTitle>
            <DialogContent>
              {alemDoMeu(papelDe(usuario.papelId)) ? (
                <Alert severity="info" sx={{ mb: 2 }}>
                  O papel desta pessoa tem permissões que você não tem — só um usuário com essas permissões pode
                  alterá-la.
                </Alert>
              ) : null}
              {erro ? (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErro(null)}>
                  {erro}
                </Alert>
              ) : null}
              <Stack spacing={2} sx={{ mt: 1, opacity: salvando ? 0.6 : 1 }}>
                <TextField
                  select
                  label="Papel"
                  value={usuario.papelId ?? ''}
                  disabled={salvando}
                  onChange={(e) => alterar(() => atualizarUsuario(usuario.id, { papel_id: e.target.value || null }))}
                >
                  <MenuItem value="">Sem papel (não vê nada)</MenuItem>
                  {papeis.map((p) => (
                    <MenuItem key={p.id} value={p.id} disabled={alemDoMeu(p)}>
                      {p.nome}
                      {alemDoMeu(p) ? ' — tem permissões que você não tem' : ''}
                    </MenuItem>
                  ))}
                </TextField>

                <FormControlLabel
                  control={
                    <Switch
                      checked={usuario.ativo}
                      disabled={salvando}
                      onChange={(e) => alterar(() => atualizarUsuario(usuario.id, { ativo: e.target.checked }))}
                    />
                  }
                  label={usuario.ativo ? 'Ativo' : 'Desativado — não acessa nada'}
                />

                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Marcas que vê
                  </Typography>
                  {veTodas(usuario) ? (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      O papel desta pessoa vê todas as marcas — não precisa liberar uma a uma.
                    </Typography>
                  ) : (
                    <Stack>
                      {marcas.map((m) => {
                        const tem = usuario.marcas.has(m.id)
                        return (
                          <FormControlLabel
                            key={m.id}
                            control={
                              <Checkbox
                                checked={tem}
                                disabled={salvando}
                                onChange={() => alterar(() => (tem ? retirarMarca(usuario.id, m.id) : liberarMarca(usuario.id, m.id)))}
                              />
                            }
                            label={m.nome}
                          />
                        )
                      })}
                      <Typography variant="caption" color="text.secondary">
                        Só aparecem as marcas que você mesmo vê.
                      </Typography>
                    </Stack>
                  )}
                </Box>

                {usuario.id !== user?.id && !alemDoMeu(papelDe(usuario.papelId)) ? <RedefinirSenha key={usuario.id} usuarioId={usuario.id} email={usuario.email} /> : null}
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setEditando(null)}>Fechar</Button>
            </DialogActions>
          </>
        ) : null}
      </Dialog>

      <Dialog open={Boolean(novo)} onClose={() => setNovo(null)} fullWidth maxWidth="xs">
        {novo ? (
          <Box
            component="form"
            onSubmit={(e) => {
              e.preventDefault()
              criar()
            }}
          >
            <DialogTitle>Novo usuário</DialogTitle>
            <DialogContent>
              <Stack spacing={2} sx={{ mt: 1 }}>
                <TextField autoFocus label="Email" type="email" value={novo.email} onChange={(e) => setNovo({ ...novo, email: e.target.value })} />
                <TextField label="Nome" value={novo.nome} onChange={(e) => setNovo({ ...novo, nome: e.target.value })} />
                <TextField
                  label="Senha provisória"
                  value={novo.senha}
                  onChange={(e) => setNovo({ ...novo, senha: e.target.value })}
                  helperText="Passe para a pessoa; ela troca no menu da conta. Mínimo de 8 caracteres."
                  InputProps={{
                    endAdornment: (
                      <Button size="small" onClick={() => setNovo({ ...novo, senha: gerarSenhaProvisoria() })} sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}>
                        Gerar outra
                      </Button>
                    ),
                  }}
                />
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setNovo(null)}>Cancelar</Button>
              <Button type="submit" variant="contained" disabled={salvando || !novo.email.trim() || novo.senha.length < 8}>
                {salvando ? <CircularProgress size={20} color="inherit" /> : 'Criar'}
              </Button>
            </DialogActions>
          </Box>
        ) : null}
      </Dialog>
    </Stack>
  )
}

/** Senha provisória nova para quem esqueceu a dele; a pessoa cria a própria no próximo acesso. */
function RedefinirSenha({ usuarioId, email }: { usuarioId: string; email: string }) {
  const [senha, setSenha] = useState<string | null>(null)
  const [salvando, setSalvando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)
  const [feita, setFeita] = useState<string | null>(null)

  const confirmar = async () => {
    if (!senha) return
    setSalvando(true)
    setErro(null)
    try {
      await redefinirSenha(usuarioId, senha)
      setFeita(senha)
      setSenha(null)
    } catch (e) {
      setErro((e as Error).message)
    } finally {
      setSalvando(false)
    }
  }

  return (
    <Box>
      <Typography variant="subtitle2" color="text.secondary">
        Senha
      </Typography>
      {feita ? (
        <Alert severity="success" sx={{ mt: 1 }}>
          Senha de {email} redefinida. Passe a provisória{' '}
          <Box component="code" sx={{ px: 0.5, fontWeight: 700 }}>
            {feita}
          </Box>{' '}
          para a pessoa: no próximo acesso ela cria a própria. Esta senha não aparece de novo.
        </Alert>
      ) : senha ? (
        <Stack spacing={1.5} sx={{ mt: 1 }}>
          <TextField
            size="small"
            label="Senha provisória"
            value={senha}
            onChange={(e) => setSenha(e.target.value)}
            error={senha.length > 0 && senha.length < 8}
            helperText="Mínimo de 8 caracteres. A senha atual da pessoa deixa de funcionar."
          />
          {erro ? <Alert severity="error">{erro}</Alert> : null}
          <Stack direction="row" spacing={1}>
            <Button variant="contained" size="small" disabled={salvando || senha.length < 8} onClick={confirmar} sx={{ textTransform: 'none' }}>
              {salvando ? 'Redefinindo…' : 'Confirmar'}
            </Button>
            <Button size="small" disabled={salvando} onClick={() => setSenha(null)} sx={{ textTransform: 'none' }}>
              Cancelar
            </Button>
          </Stack>
        </Stack>
      ) : (
        <Box sx={{ mt: 0.5 }}>
          <Button size="small" variant="outlined" onClick={() => setSenha(gerarSenhaProvisoria())} sx={{ textTransform: 'none' }}>
            Redefinir senha
          </Button>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            Para quem esqueceu a senha: gera uma provisória, e a pessoa cria a dela no próximo acesso.
          </Typography>
        </Box>
      )}
    </Box>
  )
}
